import React, { useCallback, memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import _ from "lodash";
import { Checkbox, Avatar } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { useCoursesContext } from "../../context/CoursesContext";
import {
  COURSE_ROW_HEIGHT,
  USER_TYPE_FIELDS,
  PARTICIPANT_USER_TYPES,
} from "../../modules/CoursesConstants";
import CourseRowSkeleton from "./CourseRowSkeleton";
import classes from "./CourseRow.scss";

const COURSE_ROW_STYLE = { minHeight: COURSE_ROW_HEIGHT };

const CourseRow = ({ courseId, testId }) => {
  const { t } = useI18n(["common"]);
  const {
    courseDetailsMap,
    courseCountMap,
    registerCourseRef,
    selectedCourseList,
    setSelectedCourseList,
    viewOnly,
    countsLoading,
    selectedUserTypes,
  } = useCoursesContext();

  const courseDetails = _.get(courseDetailsMap, courseId);
  const courseCount = _.get(courseCountMap, courseId);
  const isDetailsLoaded = !!courseDetails;

  const isChecked = _.some(selectedCourseList, { id: courseId });

  const handleRef = useCallback(
    element => {
      registerCourseRef(courseId, element);
    },
    [registerCourseRef, courseId]
  );

  const handleClick = useCallback(() => {
    if (viewOnly) return;

    if (isChecked) {
      setSelectedCourseList(prev =>
        _.filter(prev, item => item.id !== courseId)
      );
    } else {
      setSelectedCourseList(prev => [
        ...prev,
        { id: courseId, title: _.get(courseDetails, "title", "") },
      ]);
    }
  }, [viewOnly, isChecked, courseId, courseDetails, setSelectedCourseList]);

  const componentTestId = testId
    ? `${testId}-courseRow-${courseId}`
    : undefined;

  // State A: Fully loading — no details fetched yet
  if (!isDetailsLoaded) {
    return (
      <div
        ref={handleRef}
        data-id={courseId}
        className={classes.courseRow}
        style={COURSE_ROW_STYLE}
      >
        <CourseRowSkeleton />
      </div>
    );
  }

  const title = _.get(courseDetails, "title", "");

  return (
    <div
      ref={handleRef}
      data-id={courseId}
      className={classes.courseRow}
      style={COURSE_ROW_STYLE}
      onClick={viewOnly ? null : handleClick}
      onKeyDown={viewOnly ? null : e => onEnterOrSpaceKeyDown(e, handleClick)}
      role={viewOnly ? undefined : "button"}
      tabIndex={viewOnly ? undefined : 0}
      data-test-id={`${componentTestId}-checkbox`}
    >
      <div className={classes.leftContainer}>
        <Avatar dsVersion="2.0" name={title} shape="square" size="medium" />
        <div className={classes.courseInfo}>
          <span className={classNames("text-body", classes.courseTitle)}>
            {title}
          </span>
          {/* State B: Title loaded, counts loading */}
          {countsLoading && !courseCount ? (
            <SkeletonLoader width="360px" height="16px" />
          ) : (
            <div className={classes.countRow}>
              {_.map(selectedUserTypes, (userType, index) => {
                const count = _.get(
                  courseCount,
                  `${USER_TYPE_FIELDS[userType]}.totalCount`,
                  0
                );
                return (
                  <span
                    className={classNames("text-body-s", classes.countText)}
                    key={`${courseId}-${userType}`}
                  >
                    {index ? ", " : ""}
                    {`${count} ${t(PARTICIPANT_USER_TYPES[userType], {
                      count,
                    })}`}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {viewOnly ? null : (
        <Checkbox isChecked={isChecked} testId={componentTestId} />
      )}
    </div>
  );
};

CourseRow.propTypes = {
  courseId: PropTypes.string.isRequired,
  testId: PropTypes.string,
};

export default memo(CourseRow);
